import type {
  MilestoneType,
  StoreGroup,
  Store,
  StoreFilterParams,
  ApiResponse
} from '../types/plan-template';
import { warehouseService } from './warehouseService';
import { MOCK_MILESTONE_TYPES } from '../mock/milestoneTypeData';
import { MOCK_STORE_GROUPS, MOCK_STORES } from '../mock/storeData';
import { MOCK_BUSINESS_ACTIVITIES, MOCK_CREATORS } from '../mock/referenceData';
import { generateId } from '../utils/helpers';
import storeGroupService from './storeGroupService';
import { WHOLE_USE_MOCK } from './api';

// Simulate API delay
const delay = (ms: number = 300) => new Promise(resolve => setTimeout(resolve, ms));

export class ReferenceDataService {
  private static instance: ReferenceDataService;
  
  // Static configuration to control whether to use mock data
  private static useMock: boolean = true;

  /**
   * Get singleton instance
   */
  public static getInstance(): ReferenceDataService {
    if (!ReferenceDataService.instance) {
      ReferenceDataService.instance = new ReferenceDataService();
    }
    return ReferenceDataService.instance;
  }

  /**
   * Set whether to use mock data
   * @param useMock Whether to use mock data
   */
  public static setUseMock(useMock: boolean): void {
    ReferenceDataService.useMock = useMock;
  }

  /**
   * Get current mock usage setting
   */
  public static getUseMock(): boolean {
    return ReferenceDataService.useMock;
  }

  /**
   * Get all milestone types
   */
  static async getMilestoneTypes(): Promise<ApiResponse<MilestoneType[]>> {
    try {
      return {
          traceId: generateId(),
          success: true,
          errorMessage: null,
          flag: 'success',
          data: MOCK_MILESTONE_TYPES,
          message: '获取里程碑类型成功'
        };
    } catch (error) {
      return {
        traceId: generateId(),
        success: false,
        errorMessage: '获取里程碑类型失败',
        flag: 'fail',
        data: [],
        message: '获取里程碑类型失败',
        errorCode: 'MILESTONE_TYPES_ERROR'
      };
    }
  }

  /**
   * Get all store groups
   */
  static async getStoreGroups(): Promise<ApiResponse<StoreGroup[]>> {
    try {
      if (ReferenceDataService.useMock && WHOLE_USE_MOCK) {
        await delay();
        return {
          traceId: generateId(),
          success: true,
          errorMessage: null,
          flag: 'success',
          data: MOCK_STORE_GROUPS,
          message: '获取门店组成功'
        };
      } else {
        // Call real API
        let req: any = {};
        req.status = 'ENABLE'
        req.currentPage = 1;
        req.pageSize = 1000;
        return await storeGroupService.listStoreGroup(req);
      }
    } catch (error) {
      return {
        traceId: generateId(),
        success: false,
        errorMessage: '获取门店组失败',
        flag: 'fail',
        data: [],
        message: '获取门店组失败',
        errorCode: 'STORE_GROUPS_ERROR'
      };
    }
  }

  /**
   * Get stores with filters
   */
  static async getStores(
    filters: StoreFilterParams = {}
  ): Promise<ApiResponse<Store[]>> {
    try {
      if (ReferenceDataService.useMock && WHOLE_USE_MOCK) {
        await delay();
        let filteredStores = [...MOCK_STORES];

        // Apply search text filter
        if (filters.searchText) {
          const searchText = filters.searchText.toLowerCase();
          filteredStores = filteredStores.filter(store =>
            store.name.toLowerCase().includes(searchText) ||
            store.code.toLowerCase().includes(searchText) ||
            store.detailAddress.toLowerCase().includes(searchText)
          );
        }

        // Apply area filter
        if (filters.areas && filters.areas.length > 0) {
          filteredStores = filteredStores.filter(store =>
            filters.areas!.includes(store.area)
          );
        }

        // Apply store format filter
        if (filters.formats && filters.formats.length > 0) {
          filteredStores = filteredStores.filter(store =>
            filters.formats!.includes(store.format)
          );
        }

        // Apply cluster section filter
        if (filters.clusterSections && filters.clusterSections.length > 0) {
          filteredStores = filteredStores.filter(store =>
            filters.clusterSections!.includes(store.clusterSection)
          );
        }

        // Sort by code
        filteredStores.sort((a, b) => a.code.localeCompare(b.code));

        return {
          traceId: generateId(),
          success: true,
          errorMessage: null,
          flag: 'success',
          data: filteredStores,
          message: '获取门店列表成功'
        };
      } else {
        // Call real API
        const response = await warehouseService.listWarehouse(filters);
        return response;
      }
    } catch (error) {
      return {
        traceId: generateId(),
        success: false,
        errorMessage: '获取门店列表失败',
        flag: 'fail',
        data: [],
        message: '获取门店列表失败',
        errorCode: 'STORES_ERROR'
      };
    }
  }

  static async getStoresByCodes(
    codes: string[]
  ): Promise<ApiResponse<Store[]>> {
    try {
      if (ReferenceDataService.useMock && WHOLE_USE_MOCK) {
        await delay();
        const stores = MOCK_STORES.filter(store => codes.includes(store.code));

        return {
          traceId: generateId(),
          success: true,
          errorMessage: null,
          flag: 'success',
          data: stores,
          message: '获取门店列表成功'
        };
      } else {
        // Call real API
        const response = await warehouseService.listWarehouse({
          codes
        });
        return response;
      }
    } catch (error) {
      return {
        traceId: generateId(),
        success: false,
        errorMessage: '获取门店列表失败',
        flag: 'fail',
        data: [],
        message: '获取门店列表失败',
        errorCode: 'STORES_ERROR'
      };
    }
  }

  /**
   * Get stores by store group IDs
   */
  static async getStoresByGroupIds(
    groupIds: string[]
  ): Promise<ApiResponse<Store[]>> {
    try {
      if (ReferenceDataService.useMock && WHOLE_USE_MOCK) {
        await delay();
        // In a real implementation, this would query stores that belong to the specified groups
        // For mock purposes, we'll return a subset of stores based on store codes in groups
        const groups = MOCK_STORE_GROUPS.filter(group => groupIds.includes(group.id));
        const storeCodes = groups.flatMap(group => group.shopCodes);
        
        const stores = MOCK_STORES.filter(store => storeCodes.includes(store.code));

        return {
          traceId: generateId(),
          success: true,
          errorMessage: null,
          flag: 'success',
          data: stores,
          message: '获取门店列表成功'
        };
      } else {
        // Call real API
        const response = await storeGroupService.getStoresByGroupsIds(groupIds);
        if (response.success) {
          return {
            traceId: generateId(),
            success: true,
            errorMessage: null,
            flag: 'success',
            data: (response.data as unknown as Store[]) || [],
            message: '获取门店列表成功'
          };
        } else {
          return {
            traceId: generateId(),
            success: false,
            errorMessage: response.errorMessage || '获取门店列表失败',
            flag: 'fail',
            data: [],
            message: '获取门店列表失败',
            errorCode: 'STORES_ERROR'
          };
        }
      }
    } catch (error) {
      return {
        traceId: generateId(),
        success: false,
        errorMessage: '获取门店列表失败',
        flag: 'fail',
        data: [],
        message: '获取门店列表失败',
        errorCode: 'STORES_ERROR'
      };
    }
  }

  /**
   * Get all business activities
   */
  static async getBusinessActivities(): Promise<ApiResponse<string[]>> {
    try {
      return {
          traceId: generateId(),
          success: true,
          errorMessage: null,
          flag: 'success',
          data: MOCK_BUSINESS_ACTIVITIES,
          message: '获取业务活动列表成功'
        };
    } catch (error) {
      return {
        traceId: generateId(),
        success: false,
        errorMessage: '获取业务活动列表失败',
        flag: 'fail',
        data: [],
        message: '获取业务活动列表失败',
        errorCode: 'BUSINESS_ACTIVITIES_ERROR'
      };
    }
  }

  /**
   * Validate store IDs
   */
  // static async validateStoreIds(
  //   storeIds: string[]
  // ): Promise<ApiResponse<{
  //   validIds: string[];
  //   invalidIds: string[];
  //   validStores: Store[];
  // }>> {
  //   try {
  //     if (ReferenceDataService.useMock) {
  //       await delay();
  //       const validStores = MOCK_STORES.filter(store => storeIds.includes(store.id));
  //       const validIds = validStores.map(store => store.id);
  //       const invalidIds = storeIds.filter(id => !validIds.includes(id));

  //       return {
  //         traceId: generateId(),
  //         success: true,
  //         errorMessage: null,
  //         flag: 'success',
  //         data: {
  //           validIds,
  //           invalidIds,
  //           validStores
  //         },
  //         message: `验证完成，有效门店: ${validIds.length}，无效门店: ${invalidIds.length}`
  //       };
  //     } else {
  //       // Call real API
  //       const response = await ReferenceDataService.api.post(API_ENDPOINTS.TEMPLATES.STORES_VALIDATE, { storeIds });
  //       return response.data;
  //     }
  //   } catch (error) {
  //     return {
  //       traceId: generateId(),
  //       success: false,
  //       errorMessage: '验证门店ID失败',
  //       flag: 'fail',
  //       data: {
  //         validIds: [],
  //         invalidIds: storeIds,
  //         validStores: []
  //       },
  //       message: '验证门店ID失败',
  //       errorCode: 'STORE_VALIDATION_ERROR'
  //     };
  //   }
  // }
}

// Export singleton instance
export default ReferenceDataService.getInstance();